import React, { useState, useId, forwardRef, useImperativeHandle } from "react";

interface CheckboxProps {
	/* checked - is the value of the checkbox (starting value) */
	checked?: boolean;

	/* onCheckboxChange - function that is called when the checkbox changes state */
	onCheckboxChange?: (checked: boolean) => void;
	labelText: string;
	disabled?: boolean;
	onValidate?: (checked: boolean) => boolean;
	validationText?: string; // Added validation error message text
}

export interface CustomCheckboxHandle {
	triggerValidate: () => boolean;
}

const CustomCheckbox = forwardRef<CustomCheckboxHandle, CheckboxProps>(
	(
		{
			checked = false,
			onCheckboxChange,
			onValidate,
			labelText,
			disabled = false, // Default to false
			validationText = "" // Default to empty string
		},
		ref
	) => {
		// Generate unique ID for this component instance
		const checkboxId = useId();
		const [isChecked, setIsChecked] = useState(checked);
		const [isValid, setIsValid] = useState(true);
		const [isFocused, setIsFocused] = useState<boolean>(false);
		const [hasTriggeredValidation, setHasTriggeredValidation] =
			useState<boolean>(false);

		useImperativeHandle(ref, () => ({
			triggerValidate: () => {
				setHasTriggeredValidation(true);
				const validationResult = onValidate ? onValidate(isChecked) : true;
				setIsValid(validationResult);
				return validationResult;
			}
		}));

		// Handle checkbox change
		const handleCheckboxChange = (): void => {
			setIsChecked(!isChecked);
			onCheckboxChange?.(!isChecked);
			if (onValidate) {
				setIsValid(onValidate(!isChecked));
			}
		};

		const handleFocus = (): void => {
			setIsFocused(true);
		};

		const handleBlur = (): void => {
			setIsFocused(false);
		};

		// Tailwind color classes
		const boxBorderColor = disabled
			? "border-primary-inactive"
			: "border-white";
		const boxColor = isChecked
			? disabled
				? "bg-primary-inactive"
				: "bg-primary-active"
			: "bg-primary-background";
		const labelColor = disabled
			? "text-primary-inactive"
			: "text-primary-active";
		const validationColor = isFocused
			? "text-error-active"
			: "text-error-inactive";

		return (
			<div className="flex px-4 py-3" aria-label={`checkbox-${checkboxId}`}>
				{/* Checkbox */}
				<div>
					<span className="flex justify-center">
						<input
							type="checkbox"
							id={checkboxId}
							checked={checked}
							onChange={handleCheckboxChange}
							onFocus={handleFocus}
							onBlur={handleBlur}
							className="sr-only peer"
							disabled={disabled}
						/>
						<label
							htmlFor={checkboxId}
							className={`block w-6 h-6 border-2 ${boxBorderColor} rounded cursor-pointer ${boxColor} peer-focus:ring-1 peer-focus:ring-offset-2 peer-focus:ring-offset-primary-background peer-focus:ring-primary-active transition-all`}
						>
							<label
								htmlFor={checkboxId}
								className={`relative cursor-pointer select-none text-xl font-bold text-black left-0.5 -top-1 ${
									isChecked ? "" : "hidden"
								}`}
							>
								✓
							</label>
						</label>
					</span>
				</div>

				{/* Checkbox label */}
				<div>
					<label
						htmlFor={checkboxId}
						className={`text-sm ml-3 ${labelColor} cursor-pointer select-none inline-block whitespace-pre-wrap`}
					>
						{labelText}
					</label>

					<div
						id={`${checkboxId}-error`}
						onClick={handleCheckboxChange}
						className={`cursor-pointer pl-3 text-sm mt-1 transition-opacity duration-200 min-h-6 ${
							hasTriggeredValidation && !isValid ? "" : "hidden"
						} ${validationColor}`}
					>
						{validationText}
					</div>
				</div>
			</div>
		);
	}
);

CustomCheckbox.displayName = "CustomCheckbox";

export default CustomCheckbox;
